import * as Dialog from '@radix-ui/react-dialog'
import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router'
import { cx } from '../cx'
import { Button } from '../primitives/Button'
import { Chip } from '../primitives/Chip'
import { Icon, type IconName } from '../primitives/Icon'
import { Kbd } from '../primitives/Kbd'
import s from './AppShell.module.css'

export interface NavItem {
  to: string
  label: string
  icon: IconName
  /** A heading in the sidebar ("Sell", "Stock"). Items without one sit at the top. */
  group?: string
  /** Pinned to the phone's bottom bar (up to four). Without any, the first four are. */
  tab?: boolean
  /** Visible to builders only, not yet shareable. */
  draft?: boolean
}

export type Section = 'operate' | 'learn'

const COLLAPSE_KEY = 'app.sidebarCollapsed'

/**
 * The frame around every screen, from one registry, on every width:
 *
 * - Laptop and up: a sidebar with two tabs at its top, Operate (the brand's screens, in groups) and Learn (how to
 *   work with cpg.computer and your agent). `[` folds it to a rail.
 * - Tablet: the same as a rail of icons.
 * - Phone: a top bar, the screen, and a bottom bar with up to four screens plus More. More opens a sheet with the
 *   Operate and Learn tabs at its top.
 *
 * The shell stays mounted; only the screen inside changes. Search opens the command palette (⌘K).
 * Viewers who were shared one screen get no navigation at all (`bare`). The public demo (`demo`) carries a "Demo ·
 * example data" tag beside the brand and has no Sign out.
 */
export function AppShell(props: {
  appName: string
  section: Section
  operate: NavItem[]
  /** Learn's pages; null hides the Learn tab (viewers). */
  learn: NavItem[] | null
  /** No navigation: one shared screen. */
  bare?: boolean
  /** This is the preview (the dev server), not the live app: a Preview tag sits beside the brand. */
  preview?: boolean
  /** The public demo: a "Demo · example data" tag beside the brand, in place of Preview. */
  demo?: boolean
  user: string
  /** Absent: no Sign out (the demo, where nobody signed in). */
  onSignOut?: () => void
  onSearch: () => void
  children: ReactNode
}) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed())
  const [moreOpen, setMoreOpen] = useState(false)
  const { pathname } = useLocation()
  useEffect(() => setMoreOpen(false), [pathname])

  // `[` folds the sidebar, unless someone is typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey) return
      if ((e.target as HTMLElement | null)?.closest('input, textarea, select, [contenteditable="true"]')) return
      setCollapsed((c) => {
        writeCollapsed(!c)
        return !c
      })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const mark = <span className={s.mark}>{props.appName.trim()[0]?.toUpperCase() ?? '·'}</span>
  const tag = props.demo ? (
    <span className={s.tag} title="A demo of a brand's app, on made-up numbers. Nothing you do here is saved.">
      <Chip tone="accent">Demo · example data</Chip>
    </span>
  ) : props.preview ? (
    <span className={s.tag} title="You're on the preview. Changes show here first; the live app updates when you ship.">
      <Chip tone="warn">Preview</Chip>
    </span>
  ) : null
  const items = props.section === 'learn' && props.learn ? props.learn : props.operate
  const pinned = pickTabs(props.operate)

  if (props.bare) {
    return (
      <div className={cx(s.shell, s.bare)}>
        <header className={s.topbar}>
          {mark}
          <span className={s.topTitle}>{props.appName}</span>
          {tag}
          {props.onSignOut && (
            <Button variant="ghost" size="sm" onClick={props.onSignOut}>
              Sign out
            </Button>
          )}
        </header>
        <main className={s.main}>
          <div className={s.content}>{props.children}</div>
        </main>
      </div>
    )
  }

  return (
    <div className={cx(s.shell, collapsed && s.collapsed)}>
      <aside className={s.side} aria-label="Navigation">
        <div className={s.brand}>
          {mark}
          <span className={s.brandText}>
            <span className={s.brandName}>{props.appName}</span>
            {tag}
          </span>
          <span className={s.fold}>
            <Button
              variant="ghost"
              size="sm"
              icon="sidebar"
              iconOnly
              label={collapsed ? 'Open the sidebar ([)' : 'Fold the sidebar ([)'}
              onClick={() => {
                writeCollapsed(!collapsed)
                setCollapsed(!collapsed)
              }}
            />
          </span>
        </div>
        <button type="button" className={s.search} onClick={props.onSearch} aria-label="Search or jump to (⌘K)">
          <Icon name="search" />
          <span className={s.searchText}>Search or jump to</span>
          <Kbd>⌘K</Kbd>
        </button>
        {props.learn && <SectionTabs section={props.section} operate={props.operate} learn={props.learn} />}
        <NavList items={items} label={props.section === 'learn' ? 'Learn' : 'Screens'} />
        <div className={s.foot}>
          <span className={s.user} title={props.user}>
            {props.user}
          </span>
          {props.onSignOut && (
            <button type="button" className={s.signout} onClick={props.onSignOut} aria-label="Sign out">
              <Icon name="signout" />
              <span className={s.signoutText}>Sign out</span>
            </button>
          )}
        </div>
      </aside>

      <header className={s.topbar}>
        {mark}
        <span className={s.topTitle}>{props.appName}</span>
        {tag}
        <Button variant="ghost" icon="search" iconOnly label="Search or jump to" onClick={props.onSearch} />
      </header>

      <main className={s.main} id="main">
        <div className={s.content}>{props.children}</div>
      </main>

      <nav className={s.tabbar} aria-label="Screens">
        {pinned.map((item) => (
          <NavLink key={item.to} to={item.to} className={s.tab}>
            <Icon name={item.icon} />
            <span className={s.tabLabel}>{item.label}</span>
          </NavLink>
        ))}
        <Dialog.Root open={moreOpen} onOpenChange={setMoreOpen}>
          <Dialog.Trigger asChild>
            <button
              type="button"
              className={s.tab}
              aria-current={props.section === 'learn' || !pinned.some((p) => pathname.startsWith(p.to)) ? 'page' : undefined}
            >
              <Icon name="more" />
              <span className={s.tabLabel}>More</span>
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className={s.scrim} />
            <Dialog.Content className={s.sheet} aria-describedby={undefined}>
              <span className={s.grab} aria-hidden="true" />
              <Dialog.Title className={s.hidden}>More</Dialog.Title>
              <MoreSheet section={props.section} operate={props.operate} learn={props.learn} />
              <div className={s.sheetFoot}>
                <span className={s.user}>{props.user}</span>
                {props.onSignOut && (
                  <Button variant="ghost" size="sm" icon="signout" onClick={props.onSignOut}>
                    Sign out
                  </Button>
                )}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </nav>
    </div>
  )
}

/** Operate | Learn, at the top of the sidebar. Each tab opens its first page. */
function SectionTabs(props: { section: Section; operate: NavItem[]; learn: NavItem[] }) {
  const tabs: { id: Section; label: string; icon: IconName; to: string }[] = [
    { id: 'operate', label: 'Operate', icon: 'operate', to: props.operate[0]?.to ?? '/' },
    { id: 'learn', label: 'Learn', icon: 'learn', to: props.learn[0]?.to ?? '/learn' },
  ]
  return (
    <div className={s.sections} role="tablist" aria-label="Sections">
      {tabs.map((t) => (
        <NavLink
          key={t.id}
          to={t.to}
          role="tab"
          aria-selected={props.section === t.id}
          className={cx(s.section, props.section === t.id && s.sectionOn)}
          title={t.label}
        >
          <Icon name={t.icon} />
          <span className={s.sectionLabel}>{t.label}</span>
        </NavLink>
      ))}
    </div>
  )
}

/** The sidebar's list: ungrouped items first, then each group under its heading. */
function NavList(props: { items: NavItem[]; label: string }) {
  const groups: { name: string | undefined; items: NavItem[] }[] = []
  for (const item of props.items) {
    const g = groups.find((x) => x.name === item.group)
    if (g) g.items.push(item)
    else groups.push({ name: item.group, items: [item] })
  }
  groups.sort((a, b) => (a.name === undefined ? -1 : b.name === undefined ? 1 : 0))
  return (
    <nav className={s.nav} aria-label={props.label}>
      {groups.map((g) => (
        <div key={g.name ?? ''} className={s.group}>
          {g.name && <div className={s.groupName}>{g.name}</div>}
          {g.items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/learn'} className={s.item} title={item.label}>
              <Icon name={item.icon} />
              <span className={s.itemLabel}>{item.label}</span>
              {item.draft && <span className={s.draft}>draft</span>}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  )
}

/** The phone's More sheet: the Operate and Learn tabs at its top, then that tab's pages. */
function MoreSheet(props: { section: Section; operate: NavItem[]; learn: NavItem[] | null }) {
  const [tab, setTab] = useState<Section>(props.section)
  const items = tab === 'learn' && props.learn ? props.learn : props.operate
  return (
    <>
      {props.learn && (
        <div className={s.sections} role="tablist" aria-label="Sections">
          {(['operate', 'learn'] as const).map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={cx(s.section, tab === id && s.sectionOn)}
              onClick={() => setTab(id)}
            >
              <Icon name={id} />
              <span className={s.sectionLabel}>{id === 'operate' ? 'Operate' : 'Learn'}</span>
            </button>
          ))}
        </div>
      )}
      <nav className={s.sheetList} aria-label={tab === 'learn' ? 'Learn' : 'Screens'}>
        {items.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === '/learn'} className={s.sheetItem}>
            <Icon name={item.icon} />
            <span>{item.label}</span>
            {item.draft && <span className={s.draft}>draft</span>}
          </NavLink>
        ))}
      </nav>
    </>
  )
}

/** The phone's bottom bar: items marked `tab`, else the first four. */
export function pickTabs(items: NavItem[]): NavItem[] {
  const marked = items.filter((i) => i.tab)
  return (marked.length ? marked : items).slice(0, 4)
}

function readCollapsed(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(value: boolean) {
  try {
    localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0')
  } catch {
    // storage blocked: the fold just doesn't persist
  }
}
